import { h } from 'preact';
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import ChevronDown from 'utils/icon/chevron-down.svg';
import Storefront from "utils/storefront";
import * as queries from "utils/storefront/queries";

import ArticleCard from "../article/article-card";
import BlogFilters from "./blog-filters";

const storefront = Storefront.getInstance();

const sortValues = {
  newest: {
    key: 'UPDATED_AT',
    reverse: false,
  },
  oldest: {
    key: 'UPDATED_AT',
    reverse: true,
  },
  title: {
    key: 'TITLE',
    reverse: false,
  },
};

const BlogGrid = ({
  configuredTags,
  allTags,
  handle,
  perPage,
  articlesTotal,
  imagePlaceholder,
  fixedAspectRatio,
  blogId
}) => {
  const [sort, setSort] = useState('newest');
  const [pageInfo, setPageInfo] = useState({});
  const [selectedTags, setSelectedTags] = useState([]);
  const [articles, setArticles] = useState([]);
  const [modalIsActive, setModalIsActive] = useState(false);

  const totalArticles = parseInt(articlesTotal, 10);
  const [articleCounter, setArticleCounter] = useState({
    displayTotalArticleCount: true,
    totalArticleCount: totalArticles,
    count: totalArticles
  });

  const tags = useMemo(() =>
    allTags.filter(tag => configuredTags.includes(tag.toLowerCase())
  ), []);

  useEffect(() => {
    if (modalIsActive) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [modalIsActive]);

  useEffect(() => {
    request({});
  }, []);

  const loadMoreLabel = useMemo(() => {
    let label = window.TMW.Strings.blog.postsCount;
    label = label.replace('{{ count }}', articles.length);
    return label.replace('{{ total }}', articleCounter.count);
  }, [articles]);

  const request = useCallback((data) => {
    if (data.sort) {
      setSort(data.sort);
    }

    if (data.tags) {
      setSelectedTags([...data.tags]);
    }

    const payload = {
      tags: data.tags || selectedTags,
      sort: data.sort || sort
    };
      
    storefront.request({
      query: queries.blogGet(payload.tags),
      variables: {
        handle,
        first: perPage,
        sortKey: sortValues[payload.sort].key,
        reverse: sortValues[payload.sort].reverse,
        cursor: data.cursor || null
      },
    }).then(response => {
      if (response.data) {

        if (data.cursor) {
          setArticles([
            ...articles,
            ...response.data.blogByHandle.articles.edges.map(item =>
              item.node
            )
          ]);
        } else{
          setArticles([
            ...response.data.blogByHandle.articles.edges.map(item =>
              item.node
            )
          ]);
        }
        setPageInfo({...response.data.blogByHandle.articles.pageInfo});
      }
    });
  }, [sort, selectedTags, articles]);

  const handleSort = (e) => {
    request({
      sort: e.target.value,
    });
  }

  return (
    <div className="lg:flex lg:gap-12">
      {!!tags.length && (
        <div className="lg:w-72">
          <BlogFilters
            tags={tags}
            selectedTags={selectedTags}
            modalIsActive={modalIsActive}
            sort={sort}
            sortValues={sortValues}
            onFilter={request}
            onClose={() => setModalIsActive(false)}
            blogId={blogId}
            articleCounter={articleCounter}
            setArticleCounter={setArticleCounter}
          />
        </div>
      )}
      <div className="lg:flex-1">
        <div className="pb-5 flex items-center justify-between text-body-3 lg:text-body-2">
          <button
            className="text-cta border border-black cursor-pointer py-2.5 px-[5px] block lg:hidden"
            type="button"
            tabIndex="0"
            aria-label="Filter and Sort"
            onClick={() => setModalIsActive(true)}
            dangerouslySetInnerHTML={
              {__html:window.TMW.Strings.filters.btnLabel}
            }
          ></button>
          <span>
            {window.TMW.Strings.filters.resultsCount.replace('{{ count }}', articleCounter.count)}
          </span>
          <div className="hidden lg:block">
            <span>{window.TMW.Strings.filters.sortBy}: </span>
            <span className="relative">
              <select
                className="bg-none p-0 border-0 appearance-none pr-5"
                onChange={handleSort}
              >
                {Object.keys(sortValues).map(key => (
                  <option key={key} value={key}>
                    {window.TMW.Strings.blog.sort[key]}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-0 top-1/2 -translate-y-0.5"/>
            </span>
          </div>
        </div>
        <div className="flex-col flex gap-7 md:grid  grid-cols-2 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-12 ">
          {articles.map(article => (
            <ArticleCard
              article={article}
              fixedAspectRatio={fixedAspectRatio}
              blogUrl={`${Shopify.routes.root}blogs/${handle}`}
              imagePlaceholder={imagePlaceholder}
              key={article.id}/>
          ))}
        </div>
        {!!pageInfo.hasNextPage && (
          <div className="flex flex-col items-center justify-center gap-2.5 pt-10">
              <div className="text-body-light">
                {loadMoreLabel}
              </div>
              <button
                className="button button--primary w-full max-w-[275px]"
                onClick={() => request({cursor: pageInfo.endCursor})}
              >
                {window.TMW.Strings.filters.loadMore}
              </button>
          </div>
        )}
      </div>
    </div>
  )
};

export default BlogGrid;
